import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { Theme } from '@routific/hive';
import ColumnGroupCard from './ColumnGroupCard';
import { ExportColumnGroup } from '../../../../store/mst/models';
import { ExportColumnGroupData } from './types';

interface Props {
  groups: ExportColumnGroupData[];
  selectedKeys: Set<string>;
  hasSelection: boolean;
  hasError: boolean;
  toggleColumn: (key: string) => void;
  toggleGroup: (group: ExportColumnGroup) => void;
  isGroupChecked: (group: ExportColumnGroup) => boolean;
  isGroupIndeterminate: (group: ExportColumnGroup) => boolean;
}

const ColumnSelector: React.FC<Props> = ({
  groups,
  selectedKeys,
  hasSelection,
  hasError,
  toggleColumn,
  toggleGroup,
  isGroupChecked,
  isGroupIndeterminate,
}) => {
  const { t } = useTranslation('components');

  return (
    <Box display="flex" flexDirection="column" gap={Theme.Spacing.xsSpacing}>
      <Box
        component="label"
        sx={{ ...Theme.Typography.CaptionStrong.toObject(), color: Theme.Colors.textSecondary }}
      >
        {t('downloadCSVModal.columnsToExport')}
        <Box component="span" sx={{ color: Theme.Colors.danger5, marginLeft: '2px' }}>
          *
        </Box>
      </Box>

      <Box display="flex" flexDirection="column" gap={Theme.Spacing.smSpacing}>
        {groups.map((groupData) => (
          <ColumnGroupCard
            key={groupData.group}
            groupData={groupData}
            selectedKeys={selectedKeys}
            checked={isGroupChecked(groupData.group)}
            indeterminate={isGroupIndeterminate(groupData.group)}
            onToggleGroup={() => toggleGroup(groupData.group)}
            onToggleColumn={toggleColumn}
          />
        ))}
      </Box>

      {hasError && (
        <Box sx={{ ...Theme.Typography.Caption.toObject(), color: Theme.Colors.textInvalid }} data-cy="export-load-error">
          {t('downloadCSVModal.loadError')}
        </Box>
      )}

      {!hasError && !hasSelection && (
        <Box sx={{ ...Theme.Typography.Caption.toObject(), color: Theme.Colors.textInvalid }} data-cy="export-require-selection">
          {t('downloadCSVModal.requireSelection')}
        </Box>
      )}
    </Box>
  );
};

export default ColumnSelector;
